import React, { Fragment, useMemo, useState } from "react"
import {
  MDBTable,
  MDBTableHead,
  MDBTableBody,
  MDBIcon
} from "mdb-react-ui-kit"
import { optionsMonth } from "../general/DatosFacturacionDataTable"

/* ======================= TYPES ======================= */

type PresupuestoSubcategoria = {
  uid: string
  name: string
  months: Record<number, number>
}

export type PresupuestoCategoria = {
  uid: string
  name: string
  months: Record<number, number>
  subcategorias: PresupuestoSubcategoria[]
  type?: string
  uidCategoria?: string
}

type RealSubcategoria = {
  uid: string
  label: string
  months: number[]
}

type RealCategoria = {
  uid: string
  label: string
  months: number[]
  subcategorias: Record<string, RealSubcategoria>
}

export type CategoriaRealMap = Record<string, RealCategoria>

type Props = {
  categoriaDatosPresupuesto: PresupuestoCategoria[]
  categoriaDatosReal: CategoriaRealMap
}

type PresupuestoItem = {
  uid: string
  name: string
  months: Record<number, number>
  type?: string
  uidCategoria?: string
}

/* ======================= UTILS ======================= */

const formatCurrency = (value: number) =>
  value.toLocaleString("es-MX", {
    style: "currency",
    currency: "MXN",
    minimumFractionDigits: 2
  })

/* ======================= COMPONENT ======================= */

const CategoriaReporte = ({
  categoriaDatosPresupuesto,
  categoriaDatosReal
}: Props) => {

  /* ---------- estado colapsado ---------- */
  const [expanded, setExpanded] = useState<Record<string, boolean>>({})

  const toggleCategoria = (uid: string) => {
    setExpanded(prev => ({
      ...prev,
      [uid]: !prev[uid]
    }))
  }

  /* ---------- data ---------- */
  const reportData = useMemo(() => {
    const categorias = categoriaDatosPresupuesto.filter(
      item => !item.type
    )

    const subcategorias = categoriaDatosPresupuesto.filter(
      item => item.type === "subcategoria"
    )

    const subcategoriasByCategoria = subcategorias.reduce<
      Record<string, PresupuestoItem[]>
    >((acc, sub) => {
      if (!sub.uidCategoria) return acc
      if (!acc[sub.uidCategoria]) acc[sub.uidCategoria] = []
      acc[sub.uidCategoria].push(sub)
      return acc
    }, {})

    return categorias.map((categoria) => {
      const realCategoria = categoriaDatosReal[categoria.uid]

      const categoriaMonths = Array.from({ length: 12 }, (_, month) => {
        const ppto = Number(categoria.months?.[month] ?? 0)
        const real = Number(realCategoria?.months?.[month] ?? 0)

        return {
          ppto,
          real,
          variacion: ppto + real
        }
      })

      const subs = subcategoriasByCategoria[categoria.uid] ?? []

      const subcategoriasReporte = subs.map((sub) => {
        const realSub = realCategoria?.subcategorias?.[sub.uid]

        const months = Array.from({ length: 12 }, (_, month) => {
          const ppto = Number(sub.months?.[month] ?? 0)
          const real = Number(realSub?.months?.[month] ?? 0)

          return {
            ppto,
            real,
            variacion: ppto + real
          }
        })

        return {
          uid: sub.uid,
          name: sub.name,
          months
        }
      })

      return {
        uid: categoria.uid,
        name: categoria.name,
        months: categoriaMonths,
        subcategorias: subcategoriasReporte
      }
    })
  }, [categoriaDatosPresupuesto, categoriaDatosReal])

  /* ======================= RENDER ======================= */

  return (
    <MDBTable bordered small responsive hover>
      {/* ================= HEADER ================= */}
      <MDBTableHead>
        <tr>
          <th rowSpan={2}>Categoría</th>
          {optionsMonth.map((month) => (
            <th key={month.value} colSpan={3} className="text-center">
              {month.label}
            </th>
          ))}
        </tr>
        <tr>
          {optionsMonth.map((_, idx) => (
            <Fragment key={idx}>
              <th>PPTO</th>
              <th>REAL</th>
              <th>VAR</th>
            </Fragment>
          ))}
        </tr>
      </MDBTableHead>

      {/* ================= BODY ================= */}
      <MDBTableBody>
        {reportData.map((categoria) => {
          const isExpanded = expanded[categoria.uid]

          return (
            <Fragment key={categoria.uid}>
              {/* -------- CATEGORIA -------- */}
              <tr className="fw-bold">
                <td
                  style={{ cursor: "pointer" }}
                  onClick={() => toggleCategoria(categoria.uid)}
                >
                  <MDBIcon
                    icon={isExpanded ? "chevron-down" : "chevron-right"}
                    className="me-2"
                  />
                  {categoria.name}
                </td>

                {categoria.months.map((m, idx) => (
                  <Fragment key={idx}>
                    <td>{formatCurrency(m.ppto)}</td>
                    <td>{formatCurrency(m.real)}</td>
                    <td
                      className={
                        m.variacion < 0
                          ? "text-danger"
                          : "text-success"
                      }
                    >
                      {formatCurrency(m.variacion)}
                    </td>
                  </Fragment>
                ))}
              </tr>

              {/* -------- SUBCATEGORIAS -------- */}
              {isExpanded &&
                categoria.subcategorias.map((sub) => (
                  <tr key={sub.uid}>
                    <td className="ps-4">
                      └ {sub.name}
                    </td>

                    {sub.months.map((m, idx) => (
                      <Fragment key={idx}>
                        <td>{formatCurrency(m.ppto)}</td>
                        <td>{formatCurrency(m.real)}</td>
                        <td
                          className={
                            m.variacion < 0
                              ? "text-danger"
                              : "text-success"
                          }
                        >
                          {formatCurrency(m.variacion)}
                        </td>
                      </Fragment>
                    ))}
                  </tr>
                ))}
            </Fragment>
          )
        })}
      </MDBTableBody>
    </MDBTable>
  )
}

export default CategoriaReporte
